export const JOURNEY_UPDATE_CATEGORIES = {
  ITINERARY_CHANGED: "ITINERARY_CHANGED",
  DAY_CHANGED: "DAY_CHANGED",
  ACTIVITY_CHANGED: "ACTIVITY_CHANGED",
  TIME_CHANGED: "TIME_CHANGED",
  LOCATION_CHANGED: "LOCATION_CHANGED",
  TRANSPORT_CHANGED: "TRANSPORT_CHANGED",
  ACCOMMODATION_CHANGED: "ACCOMMODATION_CHANGED",
  IMPORTANT_UPDATE: "IMPORTANT_UPDATE",
  MESSAGE: "MESSAGE",
};

const NOTIFICATION_CATEGORY_BY_TYPE = {
  trip_updated: JOURNEY_UPDATE_CATEGORIES.ITINERARY_CHANGED,
  itinerary_update: JOURNEY_UPDATE_CATEGORIES.DAY_CHANGED,
  activity_change: JOURNEY_UPDATE_CATEGORIES.ACTIVITY_CHANGED,
  route_update: JOURNEY_UPDATE_CATEGORIES.LOCATION_CHANGED,
  transport_update: JOURNEY_UPDATE_CATEGORIES.TRANSPORT_CHANGED,
  accommodation_update: JOURNEY_UPDATE_CATEGORIES.ACCOMMODATION_CHANGED,
  important_trip_announcement: JOURNEY_UPDATE_CATEGORIES.IMPORTANT_UPDATE,
};

const TITLE_BY_CATEGORY = {
  [JOURNEY_UPDATE_CATEGORIES.ITINERARY_CHANGED]: "Journey updated",
  [JOURNEY_UPDATE_CATEGORIES.DAY_CHANGED]: "Plan changed",
  [JOURNEY_UPDATE_CATEGORIES.ACTIVITY_CHANGED]: "Activity updated",
  [JOURNEY_UPDATE_CATEGORIES.TIME_CHANGED]: "Schedule updated",
  [JOURNEY_UPDATE_CATEGORIES.LOCATION_CHANGED]: "Route updated",
  [JOURNEY_UPDATE_CATEGORIES.TRANSPORT_CHANGED]: "Transport updated",
  [JOURNEY_UPDATE_CATEGORIES.ACCOMMODATION_CHANGED]: "Accommodation updated",
  [JOURNEY_UPDATE_CATEGORIES.IMPORTANT_UPDATE]: "Important update",
  [JOURNEY_UPDATE_CATEGORIES.MESSAGE]: "New message from your trip team",
};

const ALLOWED_NOTIFICATION_TYPES = new Set(Object.keys(NOTIFICATION_CATEGORY_BY_TYPE));

function asString(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function timestampValue(value) {
  const time = Date.parse(value || "");
  return Number.isFinite(time) ? time : 0;
}

function normalizeSeverity(priority) {
  return ["low", "normal", "high", "urgent"].includes(priority) ? priority : "normal";
}

function metadataFor(item) {
  return item?.metadata && typeof item.metadata === "object" ? item.metadata : {};
}

function titleForNotification(notification, category) {
  const title = asString(notification?.title).trim();
  if (!title || title.toLowerCase().includes("changed") || title.toLowerCase().includes("updated")) {
    return TITLE_BY_CATEGORY[category] || "Journey updated";
  }
  return title;
}

function normalizeNotification(notification) {
  const type = asString(notification?.type);
  if (!ALLOWED_NOTIFICATION_TYPES.has(type)) return null;
  const metadata = metadataFor(notification);
  if (metadata.participant_visible === false || metadata.visibility === "operator" || metadata.visibility === "private") {
    return null;
  }
  if (["transport_update", "accommodation_update", "route_update"].includes(type) && metadata.participant_visible !== true) {
    return null;
  }
  const category = NOTIFICATION_CATEGORY_BY_TYPE[type];
  const id = asString(notification?.id || notification?._id || notification?.dedupe_key || `${type}:${notification?.created_at || ""}`);
  return {
    id: `notification:${id}`,
    category,
    title: titleForNotification(notification, category),
    message: asString(notification?.message || notification?.body).trim(),
    createdAt: notification?.created_at || notification?.createdAt || null,
    dayId: metadata.day_id ? String(metadata.day_id) : null,
    dayNumber: metadata.day_number ?? null,
    activityId: metadata.activity_id ? String(metadata.activity_id) : null,
    severity: normalizeSeverity(notification?.priority),
    source: "notification",
    read: !!(notification?.read || notification?.read_at),
  };
}

function normalizeMessage(message) {
  const id = asString(message?.id || message?._id || message?.client_idempotency_key || message?.created_at);
  if (!id) return null;
  const title = asString(message?.title || message?.subject).trim();
  return {
    id: `message:${id}`,
    category: JOURNEY_UPDATE_CATEGORIES.MESSAGE,
    title: TITLE_BY_CATEGORY[JOURNEY_UPDATE_CATEGORIES.MESSAGE],
    message: title || "Open trip messages for details.",
    createdAt: message?.created_at || message?.sent_at || null,
    dayId: null,
    dayNumber: null,
    activityId: null,
    severity: normalizeSeverity(message?.priority),
    source: "message",
    read: false,
  };
}

export function formatJourneyUpdateTime(value, now = new Date()) {
  const time = timestampValue(value);
  if (!time) return "";
  const deltaMs = Number(now) - time;
  if (Number.isFinite(deltaMs) && deltaMs >= 0) {
    const minutes = Math.floor(deltaMs / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
  }
  return new Date(time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function deriveJourneyUpdates(journey) {
  const notifications = Array.isArray(journey?.notifications) ? journey.notifications : [];
  const messages = Array.isArray(journey?.messages) ? journey.messages : [];
  const items = [
    ...notifications.map(normalizeNotification),
    ...messages.map(normalizeMessage),
  ].filter(Boolean);
  const deduped = Array.from(new Map(items.map((item) => [item.id, item])).values())
    .sort((a, b) => timestampValue(b.createdAt) - timestampValue(a.createdAt));
  return {
    items: deduped,
    latestAt: deduped[0]?.createdAt || null,
    unreadCount: deduped.filter((item) => item.source === "notification" && !item.read).length,
    hasUpdates: deduped.length > 0,
  };
}
